'use client';

import { useEffect, useState } from 'react';
import { useInView, InView } from 'react-intersection-observer';
import SightsCheckBoxes from './SightsCheckBoxes';
import AirportsCheckBoxes from './AirportsCheckBoxes';
import SurroundingsMapContent from './MapContent';

import { Village, VillageSurrounding } from '@/services/types';
import { ISurroundingsMapDictionary } from '@/dictionaries/components/surroundingsMap/types';
import useObserverContext from '@/hooks/useObserverContext';

type Props = {
  village: Village;
  dict: ISurroundingsMapDictionary;
};

export default function SurroundingsMap({ village, dict }: Props) {
  const defaultActiveSurroundings = village.surroundings.filter(
    ({ surrounding }) => {
      return (
        surrounding.placeCategory.code === 'locality' ||
        surrounding.placeCategory.code === 'natural_feature'
      );
    },
  );
  const [mapMarkersSurroundings, setMapMarkersSurroundings] = useState(
    defaultActiveSurroundings,
  );
  const { handleCurrentSection } = useObserverContext();
  const { ref, inView } = useInView();

  const villageLocation = {
    lat: village.location.latitude,
    lng: village.location.longitude,
  };

  const filteredSurroundings = village.surroundings.filter(
    ({ surrounding }) => {
      return surrounding.placeCategory.code !== 'airport';
    },
  );

  const airportSurroundings = village.surroundings.filter(({ surrounding }) => {
    return surrounding.placeCategory.code === 'airport';
  });

  const commomProps = {
    dict,
    mapMarkersSurroundings,
    onCheckBoxChange: (newSurroundings: VillageSurrounding[]) =>
      setMapMarkersSurroundings(newSurroundings),
  };

  useEffect(() => {
    if (inView) {
      handleCurrentSection('map');
    } else {
      handleCurrentSection('');
    }
  }, [handleCurrentSection, inView]);

  function handleInViewOnChange(inView: boolean) {
    if (inView) {
      handleCurrentSection('map');
    } else {
      handleCurrentSection('');
    }
  }

  return (
    <InView
      as="div"
      id="map-ref"
      className="max-w-default xl:max-w-full mx-auto scroll-m-20"
      onChange={(inView) => handleInViewOnChange(inView)}>
      <div className="relative mb-[30px] md:mb-[50px] xl:mb-[80px]">
        <div className="max-w-[80%] bg-white w-full lg:w-[305px] lg:absolute lg:top-[11px] lg:left-[11px] lg:p-[20px] lg:min-h-[50px] lg:z-10">
          <div>
            <div className="my-[10px] h-full">
              <SightsCheckBoxes
                villageSurroundings={filteredSurroundings}
                {...commomProps}
              />
            </div>
            <div className="my-[10px] h-full">
              <AirportsCheckBoxes
                airportSurroundings={airportSurroundings}
                {...commomProps}
              />
            </div>
          </div>
        </div>

        <SurroundingsMapContent
          dict={dict}
          surroundings={mapMarkersSurroundings}
          villageLocation={villageLocation}
        />
      </div>
      <div ref={ref} />
    </InView>
  );
}
